import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Zap, Plus, ArrowLeft, Loader2, Pencil, Trash2, Clock,
  ShieldCheck, UserPlus, Bell, ChevronRight, Mail, Settings2,
  CheckCircle2, XCircle, AlertTriangle, Moon, MessageSquare,
} from 'lucide-react';

const automationsAPI = {
  list: () => api.get('/pipeline/automations'),
  create: (data) => api.post('/pipeline/automations', data),
  update: (id, data) => api.put(`/pipeline/automations/${id}`, data),
  toggle: (id) => api.patch(`/pipeline/automations/${id}/toggle`),
  remove: (id) => api.delete(`/pipeline/automations/${id}`),
  log: (params) => api.get('/pipeline/automations/log', { params }),
  getQuietHours: () => api.get('/pipeline/automations/quiet-hours'),
  updateQuietHours: (data) => api.put('/pipeline/automations/quiet-hours', data),
};

const TRIGGER_LABELS = {
  stage_enter: 'When a lead enters a stage',
  stage_exit: 'When a lead exits a stage',
};

const ACTION_LABELS = {
  required_fields: 'Require fields to be filled',
  auto_assign: 'Auto-assign a rep',
  notify: 'Send a notification',
  send_email: 'Send an email',
  send_sms: 'Send an SMS',
};

const ACTION_ICONS = {
  required_fields: ShieldCheck,
  auto_assign: UserPlus,
  notify: Bell,
  send_email: Mail,
  send_sms: MessageSquare,
};

const FIELD_OPTIONS = [
  { value: 'contact_name', label: 'Contact Name' },
  { value: 'company_name', label: 'Company Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'estimated_value', label: 'Estimated Value' },
  { value: 'lead_source', label: 'Lead Source' },
  { value: 'lost_reason', label: 'Lost Reason' },
  { value: 'notes', label: 'Notes' },
];

const RECIPIENT_OPTIONS = [
  { value: 'assigned', label: 'Assigned rep' },
  { value: 'owner', label: 'Account owner' },
];

const EMAIL_TO_OPTIONS = [
  { value: 'lead_email', label: 'Lead\'s email address' },
  { value: 'assigned', label: 'Assigned rep\'s email' },
  { value: 'owner', label: 'Account owner\'s email' },
];

const SMS_TO_OPTIONS = [
  { value: 'lead_phone', label: 'Lead\'s phone number' },
];

const RESULT_STYLES = {
  success: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  blocked: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/30' },
  error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-950/30' },
};

// ---------------------------------------------------------------------------
// Rule summary helper
// ---------------------------------------------------------------------------
function ruleSummary(rule, stages) {
  const stageName = rule.trigger_stage_id
    ? stages.find(s => s.id === rule.trigger_stage_id)?.name || 'Unknown'
    : 'any stage';
  const trigger = rule.trigger_type === 'stage_enter'
    ? `Enters "${stageName}"`
    : `Exits "${stageName}"`;

  let action = '';
  const config = rule.action_config || {};
  if (rule.action_type === 'required_fields') {
    const fields = (config.fields || []).map(f => FIELD_OPTIONS.find(o => o.value === f)?.label || f);
    action = `Require: ${fields.join(', ')}`;
  } else if (rule.action_type === 'auto_assign') {
    action = 'Auto-assign rep';
  } else if (rule.action_type === 'notify') {
    const who = (config.recipients || []).map(r => RECIPIENT_OPTIONS.find(o => o.value === r)?.label || r);
    action = `Notify: ${who.join(', ')}`;
  } else if (rule.action_type === 'send_email') {
    const to = EMAIL_TO_OPTIONS.find(o => o.value === config.to)?.label || config.to || 'unknown';
    const delay = config.delay_minutes;
    const sendAt = config.send_at_time;
    let timing = '';
    if (sendAt) timing = ` at ${sendAt}`;
    else if (delay && delay > 0) timing = delay >= 60 ? ` after ${Math.round(delay / 60)}h` : ` after ${delay}min`;
    action = `Email to ${to}${timing}`;
  } else if (rule.action_type === 'send_sms') {
    const to = SMS_TO_OPTIONS.find(o => o.value === config.to)?.label || config.to || 'unknown';
    const delay = config.delay_minutes;
    const sendAt = config.send_at_time;
    let timing = '';
    if (sendAt) timing = ` at ${sendAt}`;
    else if (delay && delay > 0) timing = delay >= 60 ? ` after ${Math.round(delay / 60)}h` : ` after ${delay}min`;
    action = `SMS to ${to}${timing}`;
  }
  return { trigger, action };
}

// ---------------------------------------------------------------------------
// Rule Editor
// ---------------------------------------------------------------------------
const EMPTY_FORM = {
  name: '',
  trigger_type: 'stage_enter',
  trigger_stage_id: '',
  action_type: '',
  action_config: {},
  is_enabled: true,
  priority: 0,
};

function RuleEditor({ rule, stages, teamMembers, onSave, onCancel }) {
  const isEdit = !!rule;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (rule) {
      setForm({
        name: rule.name || '',
        trigger_type: rule.trigger_type || 'stage_enter',
        trigger_stage_id: rule.trigger_stage_id || '',
        action_type: rule.action_type || '',
        action_config: rule.action_config || {},
        is_enabled: rule.is_enabled ?? true,
        priority: rule.priority ?? 0,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [rule]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setConfig = (k, v) => setForm(prev => ({ ...prev, action_config: { ...prev.action_config, [k]: v } }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Rule name is required'); return; }
    if (!form.action_type) { toast.error('Select an action'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        trigger_stage_id: form.trigger_stage_id || null,
      };
      if (isEdit) {
        await automationsAPI.update(rule.id, payload);
        toast.success('Rule updated');
      } else {
        await automationsAPI.create(payload);
        toast.success('Rule created');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  // Available actions depend on trigger_type
  const actionOptions = form.trigger_type === 'stage_exit'
    ? [{ value: 'auto_assign', label: ACTION_LABELS.auto_assign }, { value: 'notify', label: ACTION_LABELS.notify }, { value: 'send_email', label: ACTION_LABELS.send_email }]
    : Object.entries(ACTION_LABELS).map(([v, l]) => ({ value: v, label: l }));

  // Reset action config when action type changes
  const handleActionTypeChange = (val) => {
    let defaultConfig = {};
    if (val === 'required_fields') defaultConfig = { fields: [] };
    else if (val === 'auto_assign') defaultConfig = { user_id: '' };
    else if (val === 'notify') defaultConfig = { recipients: ['owner'], message: 'Lead "{title}" moved to {to_stage}' };
    else if (val === 'send_email') defaultConfig = { to: 'lead_email', subject: 'Update on {title}', body: 'Hi {contact},\n\nYour inquiry "{title}" has been updated. We\'ll be in touch shortly.\n\nThank you!', suppression_hours: 24 };
    else if (val === 'send_sms') defaultConfig = { to: 'lead_phone', body: 'Hi {contact}, your inquiry "{title}" has been updated. We\'ll be in touch shortly.', suppression_hours: 24 };
    setForm(prev => ({ ...prev, action_type: val, action_config: defaultConfig }));
  };

  return (
    <div className="space-y-5" data-testid="rule-editor">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="rule-editor-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to rules
      </button>

      <h3 className="text-base font-semibold">{isEdit ? 'Edit Rule' : 'New Automation Rule'}</h3>

      {/* Rule name */}
      <div>
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rule Name</Label>
        <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Require lost reason" className="mt-1" data-testid="rule-name-input" />
      </div>

      <Separator />

      {/* Trigger */}
      <div>
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Trigger</Label>
        <Select value={form.trigger_type} onValueChange={v => setField('trigger_type', v)}>
          <SelectTrigger className="mt-1" data-testid="trigger-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(TRIGGER_LABELS).map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Stage</Label>
        <Select value={form.trigger_stage_id || '__any__'} onValueChange={v => setField('trigger_stage_id', v === '__any__' ? '' : v)}>
          <SelectTrigger className="mt-1" data-testid="trigger-stage-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Any stage</SelectItem>
            {stages.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Action */}
      <div>
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Action</Label>
        <Select value={form.action_type || '__none__'} onValueChange={v => v !== '__none__' && handleActionTypeChange(v)}>
          <SelectTrigger className="mt-1" data-testid="action-type-select">
            <SelectValue placeholder="Choose an action..." />
          </SelectTrigger>
          <SelectContent>
            {actionOptions.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Action config — required_fields */}
      {form.action_type === 'required_fields' && (
        <div data-testid="config-required-fields">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Required Fields</Label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">The lead must have these fields filled before it can enter the stage.</p>
          <div className="grid grid-cols-2 gap-2">
            {FIELD_OPTIONS.map(opt => {
              const checked = (form.action_config.fields || []).includes(opt.value);
              return (
                <label key={opt.value} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/30 transition-colors" data-testid={`field-check-${opt.value}`}>
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => {
                      const fields = form.action_config.fields || [];
                      setConfig('fields', v ? [...fields, opt.value] : fields.filter(f => f !== opt.value));
                    }}
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Action config — auto_assign */}
      {form.action_type === 'auto_assign' && (
        <div data-testid="config-auto-assign">
          <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Assign To</Label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">This rep will be automatically assigned when the trigger fires.</p>
          <Select value={form.action_config.user_id || '__none__'} onValueChange={v => v !== '__none__' && setConfig('user_id', v)}>
            <SelectTrigger className="mt-1" data-testid="assign-user-select">
              <SelectValue placeholder="Select a team member..." />
            </SelectTrigger>
            <SelectContent>
              {teamMembers.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Action config — notify */}
      {form.action_type === 'notify' && (
        <div className="space-y-3" data-testid="config-notify">
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Notify Who?</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">Select who should receive the notification. The person who moves the lead is never notified.</p>
            <div className="flex flex-col gap-2">
              {RECIPIENT_OPTIONS.map(opt => {
                const checked = (form.action_config.recipients || []).includes(opt.value);
                return (
                  <label key={opt.value} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted/30 transition-colors" data-testid={`recipient-check-${opt.value}`}>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        const recipients = form.action_config.recipients || [];
                        setConfig('recipients', v ? [...recipients, opt.value] : recipients.filter(r => r !== opt.value));
                      }}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Message</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-1">Use {'{title}'}, {'{to_stage}'}, {'{from_stage}'} as placeholders.</p>
            <Input
              value={form.action_config.message || ''}
              onChange={e => setConfig('message', e.target.value)}
              placeholder='Lead "{title}" moved to {to_stage}'
              data-testid="notify-message-input"
            />
          </div>
        </div>
      )}

      {/* Action config — send_email */}
      {form.action_type === 'send_email' && (
        <div className="space-y-3" data-testid="config-send-email">
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Send To</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">Who should receive this email?</p>
            <Select value={form.action_config.to || 'lead_email'} onValueChange={v => setConfig('to', v)}>
              <SelectTrigger data-testid="email-to-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMAIL_TO_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Subject</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-1">Use {'{title}'}, {'{contact}'}, {'{to_stage}'} as placeholders.</p>
            <Input
              value={form.action_config.subject || ''}
              onChange={e => setConfig('subject', e.target.value)}
              placeholder="Update on {title}"
              data-testid="email-subject-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Body</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-1">Plain text body. Use {'{title}'}, {'{contact}'}, {'{company}'}, {'{to_stage}'}, {'{from_stage}'} as placeholders.</p>
            <textarea
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[100px] resize-y"
              value={form.action_config.body || ''}
              onChange={e => setConfig('body', e.target.value)}
              placeholder={'Hi {contact},\n\nYour inquiry has been updated.\n\nThank you!'}
              data-testid="email-body-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Duplicate Prevention</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Don't re-send to the same lead within this many hours. Set to 0 to disable.
            </p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="720"
                className="w-24"
                value={form.action_config.suppression_hours ?? 24}
                onChange={e => setConfig('suppression_hours', parseInt(e.target.value) || 0)}
                data-testid="email-suppression-input"
              />
              <span className="text-sm text-muted-foreground">hours</span>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Timing</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">When should this email be sent?</p>
            <Select
              value={form.action_config.send_at_time ? 'send_at' : (form.action_config.delay_minutes > 0 ? 'delay' : 'immediate')}
              onValueChange={v => {
                if (v === 'immediate') {
                  setConfig('delay_minutes', 0);
                  setConfig('send_at_time', null);
                } else if (v === 'delay') {
                  setConfig('delay_minutes', form.action_config.delay_minutes || 60);
                  setConfig('send_at_time', null);
                } else if (v === 'send_at') {
                  setConfig('delay_minutes', 0);
                  setConfig('send_at_time', form.action_config.send_at_time || '09:00');
                }
              }}
            >
              <SelectTrigger data-testid="email-timing-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Send immediately</SelectItem>
                <SelectItem value="delay">Delay by a set time</SelectItem>
                <SelectItem value="send_at">Send at a specific time</SelectItem>
              </SelectContent>
            </Select>
            {form.action_config.delay_minutes > 0 && !form.action_config.send_at_time && (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  min="1"
                  max="10080"
                  className="w-24"
                  value={form.action_config.delay_minutes || 60}
                  onChange={e => setConfig('delay_minutes', parseInt(e.target.value) || 1)}
                  data-testid="email-delay-input"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
                <span className="text-xs text-muted-foreground ml-1">
                  ({form.action_config.delay_minutes >= 60
                    ? `${Math.round(form.action_config.delay_minutes / 60)}h`
                    : `${form.action_config.delay_minutes}m`})
                </span>
              </div>
            )}
            {form.action_config.send_at_time && (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="time"
                  className="w-32"
                  value={form.action_config.send_at_time || '09:00'}
                  onChange={e => setConfig('send_at_time', e.target.value)}
                  data-testid="email-send-at-input"
                />
                <span className="text-xs text-muted-foreground">(in your account timezone)</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action config — send_sms */}
      {form.action_type === 'send_sms' && (
        <div className="space-y-3" data-testid="config-send-sms">
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Send To</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">Who should receive this SMS?</p>
            <Select value={form.action_config.to || 'lead_phone'} onValueChange={v => setConfig('to', v)}>
              <SelectTrigger data-testid="sms-to-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SMS_TO_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Message</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-1">Use {'{title}'}, {'{contact}'}, {'{company}'}, {'{to_stage}'}, {'{from_stage}'} as placeholders.</p>
            <textarea
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-y"
              value={form.action_config.body || ''}
              onChange={e => setConfig('body', e.target.value)}
              placeholder={'Hi {contact}, your inquiry "{title}" has been updated.'}
              data-testid="sms-body-input"
            />
            <p className="text-xs text-muted-foreground mt-1">SMS messages over 160 characters may be split into multiple segments.</p>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Duplicate Prevention</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Don't re-send to the same lead within this many hours. Set to 0 to disable.
            </p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="720"
                className="w-24"
                value={form.action_config.suppression_hours ?? 24}
                onChange={e => setConfig('suppression_hours', parseInt(e.target.value) || 0)}
                data-testid="sms-suppression-input"
              />
              <span className="text-sm text-muted-foreground">hours</span>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Timing</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">When should this SMS be sent?</p>
            <Select
              value={form.action_config.send_at_time ? 'send_at' : (form.action_config.delay_minutes > 0 ? 'delay' : 'immediate')}
              onValueChange={v => {
                if (v === 'immediate') {
                  setConfig('delay_minutes', 0);
                  setConfig('send_at_time', null);
                } else if (v === 'delay') {
                  setConfig('delay_minutes', form.action_config.delay_minutes || 60);
                  setConfig('send_at_time', null);
                } else if (v === 'send_at') {
                  setConfig('delay_minutes', 0);
                  setConfig('send_at_time', form.action_config.send_at_time || '09:00');
                }
              }}
            >
              <SelectTrigger data-testid="sms-timing-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Send immediately</SelectItem>
                <SelectItem value="delay">Delay by a set time</SelectItem>
                <SelectItem value="send_at">Send at a specific time</SelectItem>
              </SelectContent>
            </Select>
            {form.action_config.delay_minutes > 0 && !form.action_config.send_at_time && (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  min="1"
                  max="10080"
                  className="w-24"
                  value={form.action_config.delay_minutes || 60}
                  onChange={e => setConfig('delay_minutes', parseInt(e.target.value) || 1)}
                  data-testid="sms-delay-input"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
                <span className="text-xs text-muted-foreground ml-1">
                  ({form.action_config.delay_minutes >= 60
                    ? `${Math.round(form.action_config.delay_minutes / 60)}h`
                    : `${form.action_config.delay_minutes}m`})
                </span>
              </div>
            )}
            {form.action_config.send_at_time && (
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="time"
                  className="w-32"
                  value={form.action_config.send_at_time || '09:00'}
                  onChange={e => setConfig('send_at_time', e.target.value)}
                  data-testid="sms-send-at-input"
                />
                <span className="text-xs text-muted-foreground">(in your account timezone)</span>
              </div>
            )}
          </div>
        </div>
      )}

      <Separator />

      {/* Save */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="rule-cancel-btn">Cancel</Button>
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="rule-save-btn">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
          {isEdit ? 'Update Rule' : 'Create Rule'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity Log
// ---------------------------------------------------------------------------
function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    automationsAPI.log({ limit: 50 })
      .then(res => setLogs(res.data))
      .catch(() => toast.error('Failed to load activity log'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="log-empty">
        <Clock className="w-8 h-8 mb-2 opacity-40" />
        <p className="text-sm">No automation activity yet.</p>
        <p className="text-xs mt-1">Logs will appear here when rules run.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="activity-log">
      {logs.map(entry => {
        const style = RESULT_STYLES[entry.result] || RESULT_STYLES.error;
        const Icon = style.icon;
        const details = entry.details || {};
        const time = entry.executed_at ? new Date(entry.executed_at).toLocaleString() : '';

        return (
          <div key={entry.id} className={`flex items-start gap-3 p-3 rounded-lg border ${style.bg}`} data-testid={`log-entry-${entry.id}`}>
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${style.color}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground truncate">{entry.rule_name || 'Deleted rule'}</p>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">{entry.result}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {ACTION_LABELS[entry.action_type] || entry.action_type}
              </p>
              {details.message && (
                <p className="text-xs text-muted-foreground mt-1 italic">"{details.message}"</p>
              )}
              {details.missing_fields && (
                <p className="text-xs text-muted-foreground mt-1">
                  Missing: {details.missing_fields.map(f => FIELD_OPTIONS.find(o => o.value === f)?.label || f).join(', ')}
                </p>
              )}
              {details.assigned_name && (
                <p className="text-xs text-muted-foreground mt-1">Assigned to: {details.assigned_name}</p>
              )}
              {details.recipient && (
                <p className="text-xs text-muted-foreground mt-1">To: {details.recipient}</p>
              )}
              {details.subject && (
                <p className="text-xs text-muted-foreground mt-0.5">Subject: {details.subject}</p>
              )}
              {details.body_preview && !details.subject && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">Message: {details.body_preview}</p>
              )}
              {details.error && entry.result === 'error' && (
                <p className="text-xs text-red-600 mt-0.5">{details.error}</p>
              )}
              {details.delivery === 'dev_mode' && (
                <p className="text-xs text-orange-500 mt-0.5">Sent in dev mode (not delivered)</p>
              )}
              {details.status === 'suppressed' && details.reason && (
                <p className="text-xs text-amber-600 mt-0.5">Suppressed: {details.reason}</p>
              )}
              {details.status === 'queued' && (
                <p className="text-xs text-blue-600 mt-0.5">Queued: sends {details.delivery || 'later'}</p>
              )}
              {details.status === 'delayed_quiet_hours' && (
                <p className="text-xs text-violet-600 mt-0.5">Delayed: {details.reason || 'quiet hours'}</p>
              )}
              <p className="text-[10px] text-muted-foreground/60 mt-1">{time}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quiet Hours Settings
// ---------------------------------------------------------------------------
function QuietHoursSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    automationsAPI.getQuietHours()
      .then(res => setSettings(res.data))
      .catch(() => toast.error('Failed to load quiet hours'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await automationsAPI.updateQuietHours(settings);
      setSettings(res.data);
      toast.success('Quiet hours updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }
  if (!settings) return null;

  return (
    <div className="space-y-4" data-testid="quiet-hours-settings">
      <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/10">
        <Moon className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">Quiet Hours</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Emails won't be sent during quiet hours. They'll be queued and sent at the next allowed window.
          </p>
        </div>
        <Switch
          checked={settings.enabled}
          onCheckedChange={v => setSettings(prev => ({ ...prev, enabled: v }))}
          data-testid="quiet-hours-toggle"
        />
      </div>

      {settings.enabled && (
        <div className="space-y-3 pl-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Start (no sends after)</Label>
              <Input
                type="time"
                value={settings.start || '21:00'}
                onChange={e => setSettings(prev => ({ ...prev, start: e.target.value }))}
                className="mt-1"
                data-testid="quiet-hours-start"
              />
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">End (resume sends at)</Label>
              <Input
                type="time"
                value={settings.end || '08:00'}
                onChange={e => setSettings(prev => ({ ...prev, end: e.target.value }))}
                className="mt-1"
                data-testid="quiet-hours-end"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Timezone</Label>
            <Select value={settings.timezone || 'America/New_York'} onValueChange={v => setSettings(prev => ({ ...prev, timezone: v }))}>
              <SelectTrigger className="mt-1" data-testid="quiet-hours-timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'UTC'].map(tz => (
                  <SelectItem key={tz} value={tz}>{tz.replace('America/', '').replace('_', ' ').replace('Pacific/', '')}{tz === 'America/New_York' ? ' (Eastern)' : tz === 'America/Chicago' ? ' (Central)' : tz === 'America/Denver' ? ' (Mountain)' : tz === 'America/Los_Angeles' ? ' (Pacific)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            No emails will be sent between {settings.start || '21:00'} and {settings.end || '08:00'} ({(settings.timezone || 'America/New_York').replace('America/', '').replace('_', ' ')}).
          </p>
        </div>
      )}

      <Button size="sm" onClick={handleSave} disabled={saving} className="w-full" data-testid="quiet-hours-save">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
        Save Quiet Hours
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Drawer
// ---------------------------------------------------------------------------
export function AutomationsDrawer({ open, onClose, stages, teamMembers }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('rules');  // 'rules' | 'log' | 'settings'
  const [editingRule, setEditingRule] = useState(undefined); // undefined=list, null=new, object=edit
  const [deleting, setDeleting] = useState(null);

  const fetchRules = useCallback(async () => {
    try {
      const res = await automationsAPI.list();
      setRules(res.data);
    } catch {
      toast.error('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setEditingRule(undefined);
      setTab('rules');
      fetchRules();
    }
  }, [open, fetchRules]);

  const handleToggle = async (rule) => {
    try {
      const res = await automationsAPI.toggle(rule.id);
      setRules(prev => prev.map(r => r.id === rule.id ? res.data : r));
      toast.success(`Rule ${res.data.is_enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to toggle rule');
    }
  };

  const handleDelete = async (ruleId) => {
    setDeleting(ruleId);
    try {
      await automationsAPI.remove(ruleId);
      setRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success('Rule deleted');
    } catch {
      toast.error('Failed to delete rule');
    } finally {
      setDeleting(null);
    }
  };

  const handleEditorSave = () => {
    setEditingRule(undefined);
    fetchRules();
  };

  return (
    <AppDrawer
      open={open}
      onOpenChange={v => !v && onClose()}
      title="Automations"
      description="Set up rules that run automatically when leads change stages."
      size="md"
      data-testid="automations-drawer"
    >
      {editingRule !== undefined ? (
        <RuleEditor
          rule={editingRule}
          stages={stages}
          teamMembers={teamMembers}
          onSave={handleEditorSave}
          onCancel={() => setEditingRule(undefined)}
        />
      ) : (
        <div className="space-y-4">
          {/* Tab toggle */}
          <div className="flex items-center gap-1 rounded-md border bg-muted/30 p-0.5 w-fit" data-testid="automations-tabs">
            <button
              onClick={() => setTab('rules')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${tab === 'rules' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="automations-tab-rules"
            >
              <Zap className="w-3 h-3 inline mr-1" />Rules
            </button>
            <button
              onClick={() => setTab('log')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${tab === 'log' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="automations-tab-log"
            >
              <Clock className="w-3 h-3 inline mr-1" />Activity
            </button>
            <button
              onClick={() => setTab('settings')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${tab === 'settings' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="automations-tab-settings"
            >
              <Settings2 className="w-3 h-3 inline mr-1" />Settings
            </button>
          </div>

          {tab === 'rules' ? (
            <>
              {/* Add button */}
              <Button size="sm" onClick={() => setEditingRule(null)} className="w-full justify-center gap-1.5" data-testid="add-rule-btn">
                <Plus className="w-3.5 h-3.5" /> New Rule
              </Button>

              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : rules.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="rules-empty">
                  <Zap className="w-8 h-8 mb-2 opacity-40" />
                  <p className="text-sm">No automation rules yet.</p>
                  <p className="text-xs mt-1">Create your first rule to automate pipeline workflows.</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="rules-list">
                  {rules.map(rule => {
                    const summary = ruleSummary(rule, stages);
                    const ActionIcon = ACTION_ICONS[rule.action_type] || Zap;
                    return (
                      <div
                        key={rule.id}
                        className={`group flex items-center gap-3 p-3 rounded-lg border transition-colors ${rule.is_enabled ? 'bg-background hover:bg-muted/20' : 'bg-muted/20 opacity-60'}`}
                        data-testid={`rule-card-${rule.id}`}
                      >
                        <div className="p-1.5 rounded-md bg-muted/50 shrink-0">
                          <ActionIcon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEditingRule(rule)} data-testid={`rule-edit-${rule.id}`}>
                          <p className="text-sm font-medium text-foreground truncate">{rule.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">
                            {summary.trigger} <ChevronRight className="w-3 h-3 inline mx-0.5" /> {summary.action}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleDelete(rule.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-muted-foreground hover:text-red-600 transition-all"
                            data-testid={`rule-delete-${rule.id}`}
                            disabled={deleting === rule.id}
                          >
                            {deleting === rule.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                          <Switch
                            checked={rule.is_enabled}
                            onCheckedChange={() => handleToggle(rule)}
                            data-testid={`rule-toggle-${rule.id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : tab === 'log' ? (
            <ActivityLog />
          ) : (
            <QuietHoursSettings />
          )}
        </div>
      )}
    </AppDrawer>
  );
}
